import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit'
import type { RootState } from '@/redux'
import type {
  DomainStatusEntry,
  DomainSyncStatus,
  SDKWarning,
  OutOfScopePrompt,
  PanelDataItemV1,
  EditProposal,
  SyncDomainFailure,
} from '@/types/DashboardV1.types'
import type { SyncErrorCode, SyncErrorEnvelope } from '@/types/SyncError.types'
import type {
  LastSyncPerDomainEntry,
  LastSyncSummary,
} from '@/types/LastSync.types'

// ─── State shape (§3d.7) ─────────────────────────────────────────────────────

interface DashboardViewState {
  dashboardId: string | null
  /**
   * Per-domain fetch status keyed by domainId.
   * Mirrors backend DomainExecutionResult.status.
   */
  domainStatus: Record<string, DomainStatusEntry>
  /** Active filter values used for this dashboard's fetch scope. */
  activeFilterValues: Record<string, string | string[]>
  /** For edit-mode out-of-scope prompt. */
  outOfScopePrompt: OutOfScopePrompt | null
  /** Last sync summary for the footer/status indicator. */
  lastSyncMode: 'incremental' | 'reset' | null
  lastSyncAt: string | null
  /** Whether any domain is currently syncing. */
  isSyncing: boolean

  /**
   * Epoch ms (Date.now() + retryAfterSec*1000) for the RATE_LIMIT slot.
   * Set when a RATE_LIMIT envelope arrives. Cleared by clearRateLimit() or
   * setDashboardError(null).
   *
   * TDD ref: §2.3. Brand guide §34.3.
   */
  rateLimitedUntil: number | null

  /**
   * Vendor name from the most recent RATE_LIMIT envelope (e.g. "campminder").
   * Stored alongside rateLimitedUntil so RateLimitBanner can interpolate the
   * real vendor name into "Hold on — {vendor} is rate-limiting us." instead
   * of guessing from authExpiredEnvelope or falling back to the literal
   * "Vendor". Cleared whenever rateLimitedUntil is cleared.
   *
   * PR #335 review fix: previously the RATE_LIMIT vendor was discarded by
   * setDashboardError, leaving Canvas to derive vendor from
   * authExpiredEnvelope?.vendor — which is null in the canonical RATE_LIMIT-only
   * scenario.
   */
  rateLimitVendor: string | null

  /**
   * AUTH_EXPIRED slot — set when any domain emits an AUTH_EXPIRED envelope.
   * Cleared by clearAuthExpired() (called on vendor reconnect success OR on
   * Reconnect CTA click). Independent of rateLimitedUntil so both banners
   * can be active simultaneously.
   *
   * Brand guide §34.3 (independent-slots multi-banner stacking rule).
   */
  authExpiredEnvelope: SyncErrorEnvelope | null

  /**
   * Accumulator for SCHEMA_DRIFT, TRANSIENT_5XX, NETWORK, PLATFORM envelopes.
   * Pushed per-envelope; deduped by `${vendor}:${code}` so the same source
   * cannot double-stack. Cleared at the start of each new sync attempt via
   * clearDomainErrors().
   *
   * BUSINESS_EMPTY is panel-chip only and is NOT pushed here.
   *
   * Brand guide §34.3 (independent-slots multi-banner stacking rule).
   */
  domainErrorStack: SyncErrorEnvelope[]

  /**
   * Per-panel display data keyed by panelId.
   * Populated by loadDashboardDataV1Thunk after GET /v1/dashboards/:id/data.
   */
  panelDataMap: Record<string, PanelDataItemV1>

  // ── Conversational edit flow (Track 2 — mockups 17–21) ──────────────────────
  /**
   * When non-null, the canvas is in panel-edit mode: the identified panel
   * is the edit target; all other panels are dimmed (opacity-55, aria-hidden,
   * pointer-events-none). The chat panel shows the PanelContextBanner and
   * pre-loads the edit greeting.
   */
  editingPanelId: string | null
  /**
   * The panel's display title for the context banner.
   * Set alongside editingPanelId so the banner renders without looking up
   * the panel definition from the panels slice.
   */
  editingPanelTitle: string | null
  /**
   * Chat mode for the viewing-mode AI chat.
   * 'free' — normal dashboard-level chat (default).
   * 'panel' — scoped to a specific panel (editingPanelId is non-null).
   */
  chatMode: 'free' | 'panel'
  /**
   * The most recent AI-proposed edit. Rendered as an inline proposal card
   * inside the AI message bubble (mockup 18). Null until the AI responds
   * with a propose_panels or modify_panel tool call.
   */
  pendingEditProposal: EditProposal | null

  // ── Slice 3.2 — Sync error UX with panel preservation (§0.3 invariant) ─────
  /**
   * Domains whose sync was SKIPPED on the most recent `sync:dashboard:complete`
   * event because the backing credential probed as `unavailable`. Populated
   * by the `markDashboardComplete` reducer from `payload.domains_failed` and
   * cleared on the next sync's start.
   *
   * **Invariant (TDD §0.3, CEO directive 2026-05-26 verbatim):** the BE
   * preserved cached `dashboard_panel_data` rows bit-identically for these
   * domains. The FE MIRRORS that contract — `markDashboardComplete` does NOT
   * clear, blank, or overwrite per-panel `panelDataMap` slices for any
   * `domainId` in this set. The ONLY visible per-panel marker is the
   * "(cached)" suffix on the panel date meta (brand guide §37f).
   *
   * Drives:
   *   - `<SyncErrorBanner />` mount above the panel grid.
   *   - "(cached)" suffix on affected panels' date meta (PanelStaleNote).
   *   - Sync button `variant="outline"` residual signal (§22 partial failure).
   */
  domainsFailed: SyncDomainFailure[]

  /**
   * Whether the user has dismissed the current `<SyncErrorBanner />`. Reset
   * to `false` on every new sync start (so a subsequent failed sync re-shows
   * the banner). Residual signals (sync-status dot, "(cached)" suffix) persist
   * after dismiss per brand guide §37f.
   */
  syncErrorDismissed: boolean
}

const initialState: DashboardViewState = {
  dashboardId: null,
  domainStatus: {},
  activeFilterValues: {},
  outOfScopePrompt: null,
  lastSyncMode: null,
  lastSyncAt: null,
  isSyncing: false,
  rateLimitedUntil: null,
  rateLimitVendor: null,
  authExpiredEnvelope: null,
  domainErrorStack: [],
  panelDataMap: {},
  editingPanelId: null,
  editingPanelTitle: null,
  chatMode: 'free',
  pendingEditProposal: null,
  domainsFailed: [],
  syncErrorDismissed: false,
}

// ─── Slice ────────────────────────────────────────────────────────────────────

const dashboardViewSlice = createSlice({
  name: 'dashboardView',
  initialState,
  reducers: {
    setDashboardId(state, action: PayloadAction<string | null>) {
      state.dashboardId = action.payload
    },

    startDomainFetch(state, action: PayloadAction<string>) {
      const domainId = action.payload
      const existing = state.domainStatus[domainId]
      state.domainStatus[domainId] = {
        status: 'fetching' as DomainSyncStatus,
        lastSyncedAt: existing?.lastSyncedAt ?? null,
        warnings: existing?.warnings ?? [],
        // Preserve prior error fields so the banner stays visible during retry.
        // Cleared only on success (markDomainComplete) or explicit setDomainError.
        errorCode: existing?.errorCode,
        errorUserMessage: existing?.errorUserMessage,
        failedRefId: existing?.failedRefId,
      }
      state.isSyncing = true
      // Slice 3.2: a fresh sync attempt re-enables the §37f banner. Residual
      // signals (sync-status dot, "(cached)" suffix) reset implicitly when
      // markDashboardComplete overwrites domainsFailed with the new event.
      state.syncErrorDismissed = false
    },

    setDomainStatus(
      state,
      action: PayloadAction<{ domainId: string; status: DomainSyncStatus }>
    ) {
      const { domainId, status } = action.payload
      if (!state.domainStatus[domainId]) {
        state.domainStatus[domainId] = {
          status,
          lastSyncedAt: null,
          warnings: [],
        }
      } else {
        state.domainStatus[domainId].status = status
      }
    },

    setDomainWarnings(
      state,
      action: PayloadAction<{ domainId: string; warnings: SDKWarning[] }>
    ) {
      const { domainId, warnings } = action.payload
      if (state.domainStatus[domainId]) {
        state.domainStatus[domainId].warnings = warnings
      }
    },

    setDomainError(
      state,
      action: PayloadAction<{
        domainId: string
        errorCode: string
        errorUserMessage: string
        failedRefId?: string
      }>
    ) {
      const { domainId, errorCode, errorUserMessage, failedRefId } = action.payload
      state.domainStatus[domainId] = {
        ...(state.domainStatus[domainId] ?? { lastSyncedAt: null, warnings: [] }),
        status: 'error',
        errorCode,
        errorUserMessage,
        failedRefId,
      }
    },

    markDomainComplete(
      state,
      action: PayloadAction<{
        domainId: string
        warnings: SDKWarning[]
        syncMode: 'incremental' | 'reset'
        /** ISO-8601 timestamp computed at dispatch time — keeps reducer pure. */
        syncedAt: string
      }>
    ) {
      const { domainId, warnings, syncMode, syncedAt } = action.payload
      state.domainStatus[domainId] = {
        ...(state.domainStatus[domainId] ?? { lastSyncedAt: null }),
        status: 'success',
        warnings,
        lastSyncedAt: syncedAt,
        errorCode: undefined,
        errorUserMessage: undefined,
        failedRefId: undefined,
      }
      state.lastSyncMode = syncMode
    },

    markDashboardComplete(
      state,
      action: PayloadAction<{
        summary: { successDomains: number; errorDomains: number; totalWarnings: number }
        syncMode: 'incremental' | 'reset'
        /** ISO-8601 timestamp computed at dispatch time — keeps reducer pure. */
        syncedAt: string
        /**
         * Slice 3.2 — credential-aware availability.
         * Domains whose sync was SKIPPED because the backing credential
         * probed as `unavailable`. Optional/empty when all domains synced
         * normally (or all sync failures were non-availability errors,
         * which flow through `setDashboardError`).
         */
        domainsFailed?: SyncDomainFailure[]
      }>
    ) {
      state.isSyncing = false
      state.lastSyncMode = action.payload.syncMode
      state.lastSyncAt = action.payload.syncedAt

      // ────────────────────────────────────────────────────────────────────────
      // Slice 3.2 — credential-aware availability + panel preservation invariant
      // ────────────────────────────────────────────────────────────────────────
      //
      // **TDD §0.3 panel data preservation invariant (CEO directive 2026-05-26):**
      //
      //   > "We should never remove data that was already there; that's a bad
      //   >  experience."
      //   >
      //   > "I had a dashboard with data from 2 years ago. I would like to
      //   >  come back now to see that data, but my credentials are no longer
      //   >  valid. I should still be able to see the data from 2 years ago
      //   >  without empty panels."
      //
      // This reducer is the FE expression of the §0.3 invariant. The BE's sync
      // pipeline guarantees that `dashboard_panel_data` rows are NOT cleared,
      // overwritten, or upserted for any domain in `payload.domains_failed` —
      // those rows retain their pre-sync `data` JSONB checksum bit-identically.
      // The FE counterpart of that guarantee is implemented HERE: for any
      // `domainId` listed in `payload.domainsFailed`, this reducer MUST NOT
      // clear, blank, or otherwise mutate any entry in `state.panelDataMap`.
      // Note: `panelDataMap` is keyed by panelId, not domainId — the
      // panel→domain relationship lives on `DashboardPanelV1.domainId` in the
      // panels definition slice. This reducer has no access to that mapping
      // and does not need it: the invariant is satisfied by non-action
      // (leaving `panelDataMap` entirely untouched).
      //
      // The current implementation satisfies this trivially because
      // `markDashboardComplete` was never authored to touch `panelDataMap` —
      // panel data is refreshed by a separate dispatch of `setPanelDataMap`
      // (from `loadDashboardDataV1Thunk`), whose BE response will faithfully
      // echo the preserved cached rows.
      //
      // The §37f banner gating below records WHICH domains were skipped so:
      //   1. `<SyncErrorBanner />` mounts above the panel grid.
      //   2. PanelCanvasV1 appends the "(cached)" suffix to those panels'
      //      date-meta footer (the ONLY visible per-panel marker per §37f).
      //
      // DO NOT "optimise" this to clear `domainStatus[domainId]`,
      // `panelDataMap[panelId]`, or any panel-display state on the basis of
      // `payload.domainsFailed`. The whole point of the §0.3 invariant is that
      // failure leaves the displayed data UNTOUCHED.
      //
      // Cross-references:
      //   - TDD §0.3 (panel data preservation invariant — the rule this enforces)
      //   - TDD §3.3.2 (BE pipeline contract: BE never wrote, FE never overwrites)
      //   - TDD §13.2 (FE visual contract: banner + "(cached)" suffix)
      //   - Brand guide §37f (Sync Error Banner — Post-Sync Failure with Panel
      //     Preservation)
      state.domainsFailed = action.payload.domainsFailed ?? []
      // Fresh sync result re-enables the banner. The dismiss flag is also
      // re-armed at startDomainFetch — this is a belt-and-braces guard for
      // the case where two consecutive failed syncs land without an
      // intervening sync-start signal.
      state.syncErrorDismissed = false
    },

    /**
     * Slice 3.2: user dismissed the §37f sync-error banner via the X button.
     * Residual signals (sync-status dot, Sync button outline variant,
     * "(cached)" suffix on panel date meta) remain in place — the banner is
     * just hidden until the next sync attempt. `domainsFailed` stays populated
     * so the (cached) suffix still renders.
     *
     * Reset to `false` on every `startDomainFetch` so a subsequent failed sync
     * brings the banner back. Brand guide §37f, TDD §13.2.
     */
    dismissSyncError(state) {
      state.syncErrorDismissed = true
    },

    setActiveFilterValues(state, action: PayloadAction<Record<string, string | string[]>>) {
      state.activeFilterValues = action.payload
    },

    setOutOfScopePrompt(state, action: PayloadAction<OutOfScopePrompt>) {
      state.outOfScopePrompt = action.payload
    },

    clearOutOfScopePrompt(state) {
      state.outOfScopePrompt = null
    },

    /**
     * Route a sync error envelope to the correct independent state slot.
     *
     * Router behaviour (brand guide §34.3, CEO directive 2026-05-05):
     *   null              → clear ALL three slots (rateLimitedUntil, authExpiredEnvelope, domainErrorStack)
     *   RATE_LIMIT        → set rateLimitedUntil = Date.now() + retryAfterSec*1000
     *   AUTH_EXPIRED      → set authExpiredEnvelope
     *   SCHEMA_DRIFT |
     *   TRANSIENT_5XX |
     *   NETWORK |
     *   PLATFORM          → push to domainErrorStack, deduped by `${vendor}:${code}`
     *   BUSINESS_EMPTY    → no-op (panel-chip only, never canvas-level)
     *
     * Date.now() is called here (in the reducer) because reducers are called
     * synchronously by dispatch — this is the correct seam for impure time reads
     * while keeping components pure.
     *
     * TDD ref: §2.3. Brand guide §34.3.
     */
    setDashboardError(state, action: PayloadAction<SyncErrorEnvelope | null>) {
      const envelope = action.payload

      if (envelope === null) {
        // Clear all slots
        state.rateLimitedUntil = null
        state.rateLimitVendor = null
        state.authExpiredEnvelope = null
        state.domainErrorStack = []
        return
      }

      if (envelope.code === 'RATE_LIMIT') {
        if (envelope.retryAfterSec) {
          state.rateLimitedUntil = Date.now() + envelope.retryAfterSec * 1000
          state.rateLimitVendor = envelope.vendor
        } else {
          // PR #335 review fix: BE shouldn't ship a RATE_LIMIT envelope
          // without retryAfterSec, but if it does we no-op rather than
          // surfacing a never-clearing banner. Warn so the misshape is
          // visible during dev/QA rather than swallowed silently.
          console.warn(
            'setDashboardError: RATE_LIMIT envelope without retryAfterSec ignored',
            envelope,
          )
        }
        return
      }

      // AUTH_EXPIRED (time-bound) and AUTH_INVALID (bad/wrong/revoked) both
      // route to the same authExpiredEnvelope slot — the canvas-level banner
      // and per-panel chip render different copy based on envelope.code,
      // but the recovery action (Reconnect) is the same. Slice F bugbash
      // 2026-05-06.
      if (
        envelope.code === 'AUTH_EXPIRED' ||
        envelope.code === 'AUTH_INVALID'
      ) {
        state.authExpiredEnvelope = envelope
        return
      }

      if (
        envelope.code === 'SCHEMA_DRIFT' ||
        envelope.code === 'TRANSIENT_5XX' ||
        envelope.code === 'NETWORK' ||
        envelope.code === 'PLATFORM'
      ) {
        // Dedupe by `${vendor}:${code}` so the same source cannot double-stack.
        const key = `${envelope.vendor}:${envelope.code}`
        const alreadyPresent = state.domainErrorStack.some(
          e => `${e.vendor}:${e.code}` === key
        )
        if (!alreadyPresent) {
          state.domainErrorStack.push(envelope)
        }
        return
      }

      // BUSINESS_EMPTY → no-op (panel-chip only)
    },

    /**
     * Clear the RATE_LIMIT slot only.
     * Use when the countdown expires or the user manually clears the banner.
     */
    clearRateLimit(state) {
      state.rateLimitedUntil = null
      state.rateLimitVendor = null
    },

    /**
     * Clear the AUTH_EXPIRED slot only.
     * Called by AuthExpiredBanner's reconnect CTA and by syncDashboardThunk
     * on per-vendor success.
     */
    clearAuthExpired(state) {
      state.authExpiredEnvelope = null
    },

    /**
     * Empty the domainErrorStack.
     * Called at the start of each new sync attempt so stale domain errors
     * from prior syncs do not persist into the new one.
     */
    clearDomainErrors(state) {
      state.domainErrorStack = []
    },

    /**
     * @deprecated Use `setDashboardError` with a `SyncErrorEnvelope` instead.
     *
     * Compat shim — synthesizes an AUTH_EXPIRED envelope from the legacy
     * `'401' | null` pattern. Retained for call sites that have not yet been
     * migrated away from the string-based API. With the new router-style
     * setDashboardError, dispatching AUTH_EXPIRED still correctly routes to
     * the authExpiredEnvelope slot.
     *
     * TODO (cleanup): audit remaining callers and migrate them to dispatch
     * setDashboardError directly, then remove this action.
     * Flagged: plans/2026-05-05-sync-error-handling-and-observability.md §6.
     */
    setSyncFailedStatus(state, action: PayloadAction<'401' | null>) {
      if (action.payload === '401') {
        state.authExpiredEnvelope = {
          code: 'AUTH_EXPIRED',
          message: 'Your CampMinder connection expired. Reconnect to keep syncing.',
          vendor: 'campminder',
        }
      } else {
        state.authExpiredEnvelope = null
        state.rateLimitedUntil = null
        state.domainErrorStack = []
      }
    },

    setSyncing(state, action: PayloadAction<boolean>) {
      state.isSyncing = action.payload
    },

    /**
     * Stores the full panel data response from GET /v1/dashboards/:id/data.
     * Keyed by panelId for O(1) lookup in PanelCanvasV1.
     */
    setPanelDataMap(state, action: PayloadAction<PanelDataItemV1[]>) {
      const map: Record<string, PanelDataItemV1> = {}
      for (const item of action.payload) {
        map[item.panelId] = item
      }
      state.panelDataMap = map
    },

    // ── Conversational edit flow actions (Track 2 — mockups 17–21) ─────────────

    /**
     * Enter panel-scoped edit mode.
     * Dims all non-target panels. Opens chat with PanelContextBanner.
     */
    enterEditMode(
      state,
      action: PayloadAction<{ panelId: string; panelTitle: string }>
    ) {
      state.editingPanelId = action.payload.panelId
      state.editingPanelTitle = action.payload.panelTitle
      state.chatMode = 'panel'
      state.pendingEditProposal = null
      state.outOfScopePrompt = null
    },

    /**
     * Exit panel-scoped edit mode.
     * Restores all panels to full opacity. Clears pending proposal.
     * Chat returns to dashboard-level context (chatMode: 'free').
     */
    exitEditMode(state) {
      state.editingPanelId = null
      state.editingPanelTitle = null
      state.chatMode = 'free'
      state.pendingEditProposal = null
      state.outOfScopePrompt = null
    },

    /**
     * Store the AI's proposed edit (from propose_panels or modify_panel tool call).
     * The proposal card is rendered inline in the AI message bubble (mockup 18).
     */
    proposeEdit(state, action: PayloadAction<EditProposal>) {
      state.pendingEditProposal = action.payload
    },

    /**
     * User accepted the proposed edit.
     * Panel is added/modified on canvas; proposal card collapses; edit mode exits.
     * Mirrors exitEditMode: also clears any pending out-of-scope prompt so the
     * scope callout does not persist in state after the edit cycle completes.
     */
    acceptEdit(state) {
      state.pendingEditProposal = null
      state.editingPanelId = null
      state.editingPanelTitle = null
      state.chatMode = 'free'
      state.outOfScopePrompt = null
    },

    /**
     * User dismissed the proposed edit.
     * Proposal card collapses; chat re-enables for further requests.
     */
    dismissEdit(state) {
      state.pendingEditProposal = null
    },

    /**
     * Show the out-of-scope prompt with missing filter context.
     * Replaces setOutOfScopePrompt — prefer this in conversational edit contexts
     * for clarity; setOutOfScopePrompt is preserved for backward compat.
     */
    showOutOfScopePrompt(state, action: PayloadAction<OutOfScopePrompt>) {
      state.outOfScopePrompt = action.payload
    },

    /**
     * User accepted scope expansion.
     * Clears the out-of-scope prompt. Canvas triggers filter-expand + re-fetch.
     */
    expandScope(state) {
      state.outOfScopePrompt = null
    },

    /**
     * User cancelled scope expansion.
     * Clears the out-of-scope prompt without changing filters.
     */
    cancelScopeExpansion(state) {
      state.outOfScopePrompt = null
    },

    reset() {
      return initialState
    },
  },
})

export const dashboardViewReducer = dashboardViewSlice.reducer
export const dashboardViewActions = dashboardViewSlice.actions
export default dashboardViewSlice

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectDomainStatus =
  (domainId: string) =>
  (state: RootState): DomainStatusEntry | undefined =>
    state.dashboardView.domainStatus[domainId]

/**
 * Returns all domains that are banner-worthy:
 *   - status === 'error', OR
 *   - status === 'fetching' AND errorCode != null (retry-in-flight from a prior error)
 *
 * The second case keeps the banner visible while the retry thunk is running so the
 * user sees "Retrying…" instead of the banner disappearing mid-flight.
 *
 * Used to render one DomainErrorBanner per failed domain.
 *
 * Memoised with createSelector (§3d.7): re-runs only when domainStatus reference
 * changes, preventing the "returned a different reference" Redux DevTools warning.
 */
export const selectFailedDomains = createSelector(
  (state: RootState) => state.dashboardView.domainStatus,
  (
    domainStatus,
  ): {
    domainId: string
    errorUserMessage: string
    errorCode: SyncErrorCode | null
  }[] =>
    Object.entries(domainStatus)
      .filter(
        ([, entry]) =>
          (entry.status === 'error' ||
            (entry.status === 'fetching' && entry.errorCode != null)) &&
          // Slice F bugbash 2026-05-06: AUTH categories are surfaced
          // exclusively via the canvas-level AuthExpiredBanner. The
          // DomainErrorBannerStack is for SCHEMA_DRIFT / TRANSIENT_5XX /
          // NETWORK / PLATFORM only — including AUTH here causes
          // duplicate banners (one canvas-level, one per-domain).
          entry.errorCode !== 'AUTH_EXPIRED' &&
          entry.errorCode !== 'AUTH_INVALID'
      )
      .map(([domainId, entry]) => ({
        domainId,
        errorUserMessage:
          entry.errorUserMessage ?? 'An unexpected error occurred.',
        // PR #335 review fix: surface errorCode so DomainErrorBannerStack can
        // route per-category copy/icon/colour (SCHEMA_DRIFT vs NETWORK vs
        // PLATFORM vs TRANSIENT_5XX). Previously this was dropped and every
        // banner fell through to the default TRANSIENT_5XX-styled variant.
        // errorCode is cast: workflow-era taxonomy-foreign codes (e.g.
        // 'RETRY_REQUEST_FAILED' from DashboardViewService) round-trip
        // through here as the cast value; getCategoryConfig handles unknown
        // codes via its default arm.
        errorCode: (entry.errorCode ?? null) as SyncErrorCode | null,
      }))
)

export const selectActiveSyncMode = (state: RootState): 'incremental' | 'reset' | null =>
  state.dashboardView.lastSyncMode

export const selectOutOfScopePrompt = (state: RootState): OutOfScopePrompt | null =>
  state.dashboardView.outOfScopePrompt

export const selectIsSyncing = (state: RootState): boolean =>
  state.dashboardView.isSyncing

export const selectLastSyncAt = (state: RootState): string | null =>
  state.dashboardView.lastSyncAt

/**
 * @deprecated Compat selector — returns '401' when authExpiredEnvelope is set.
 * Migrate consumers to selectAuthExpiredEnvelope.
 */
export const selectSyncFailedStatus = (state: RootState): '401' | null =>
  state.dashboardView.authExpiredEnvelope != null ? '401' : null

/**
 * Returns the epoch-ms timestamp when the rate-limit window clears.
 * Null when no rate-limit is active. Components compute secondsLeft as:
 *   Math.max(0, Math.ceil((rateLimitedUntil - Date.now()) / 1000))
 */
export const selectRateLimitedUntil = (state: RootState): number | null =>
  state.dashboardView.rateLimitedUntil

/**
 * Returns the vendor name from the active RATE_LIMIT envelope, e.g.
 * "campminder". Null when no rate-limit is active. Used by RateLimitBanner
 * for the "Hold on — {vendor} is rate-limiting us." copy.
 *
 * PR #335 review fix.
 */
export const selectRateLimitVendor = (state: RootState): string | null =>
  state.dashboardView.rateLimitVendor

/**
 * Returns the AUTH_EXPIRED envelope when active, or null.
 * Drives AuthExpiredBanner mount. Cleared by clearAuthExpired() or
 * setDashboardError(null).
 */
export const selectAuthExpiredEnvelope = (state: RootState): SyncErrorEnvelope | null =>
  state.dashboardView.authExpiredEnvelope

/**
 * Returns the current domainErrorStack (SCHEMA_DRIFT, TRANSIENT_5XX, NETWORK, PLATFORM).
 * Each entry is a distinct `${vendor}:${code}` combination. BUSINESS_EMPTY is never
 * included here (panel-chip only).
 */
export const selectDomainErrorStack = (state: RootState): SyncErrorEnvelope[] =>
  state.dashboardView.domainErrorStack

/**
 * True when ANY of the three error slots is active:
 *   rateLimitedUntil != null | authExpiredEnvelope != null | domainErrorStack.length > 0
 *
 * Use this when you need "is there any blocker" without caring which specific one.
 */
export const selectHasAnyBlocker = (state: RootState): boolean =>
  state.dashboardView.rateLimitedUntil != null ||
  state.dashboardView.authExpiredEnvelope != null ||
  state.dashboardView.domainErrorStack.length > 0

/**
 * @deprecated Use selectRateLimitedUntil, selectAuthExpiredEnvelope, or
 * selectDomainErrorStack for specific error slots. This selector is retained
 * only for selectLastSyncSummary's rate_limited branch which reads
 * rateLimitedUntil directly.
 *
 * Returns a synthetic envelope for consumers that still need a single
 * SyncErrorEnvelope. Prefers authExpiredEnvelope, then the first entry in
 * domainErrorStack. Returns null when no error is active.
 */
export const selectDashboardError = (state: RootState): SyncErrorEnvelope | null => {
  const { authExpiredEnvelope, domainErrorStack } = state.dashboardView
  if (authExpiredEnvelope != null) return authExpiredEnvelope
  if (domainErrorStack.length > 0) return domainErrorStack[0]
  return null
}

export const selectAllDomainStatuses = (
  state: RootState
): Record<string, DomainStatusEntry> => state.dashboardView.domainStatus

export const selectPanelDataMap = (
  state: RootState
): Record<string, PanelDataItemV1> => state.dashboardView.panelDataMap

// ─── LastSyncSummary types (Slice D) ─────────────────────────────────────────
// PR #335 review fix: types extracted to src/types/LastSync.types.ts so they
// live where the rest of the project's shared types do (frontend prompt rule).

/**
 * Memoized selector that derives the 7-state LastSyncSummary from:
 *   - state.dashboardView.domainStatus
 *   - selectDashboardError (top-level envelope)
 *   - selectRateLimitedUntil (epoch ms or null)
 *
 * State machine priority (per plan §3 D3):
 *   1. rate_limited  — rateLimitedUntil is non-null and in the future
 *   2. syncing       — any domain has status 'fetching'
 *   3. never_synced  — no domains at all, OR all domains have no lastSyncedAt and no errors
 *   4. total_failure — every domain has status 'error'
 *   5. partial_failure — at least one but not all domains have status 'error'
 *   6. synced_stale  — max lastSyncedAt is > 1 hour ago
 *   7. synced_recent — otherwise
 *
 * TDD ref: §6.4.
 */
export const selectLastSyncSummary = createSelector(
  [
    (state: RootState) => state.dashboardView.domainStatus,
    selectRateLimitedUntil,
    // Chip-honesty (CEO 2026-05-29): also consult the top-level `isSyncing`
    // flag set by `setSyncing(true)` in syncDashboardThunk / resetDashboardThunk.
    // Without this, the chip read ONLY per-domain status — which is set by
    // socket events (`sync:domain:progress`) that arrive AFTER the BE
    // orchestrator entry. With a multi-minute fan-out sync this meant the
    // chip lied with "Synced just now" / "Not yet synced" for the whole sync
    // window. The chip MUST honor `isSyncing` as the source of truth even
    // when per-domain entries haven't been populated yet OR have stale
    // 'success' status from a previous sync.
    (state: RootState) => state.dashboardView.isSyncing,
  ],
  (domainStatus, rateLimitedUntil, topLevelIsSyncing): LastSyncSummary => {
    // 1. Rate-limited — overrides everything.
    // Reads rateLimitedUntil directly (independent slot); no selectDashboardError needed.
    const isRateLimited = rateLimitedUntil != null && Date.now() < rateLimitedUntil
    if (isRateLimited) {
      const retryAfterSec = Math.max(
        0,
        Math.ceil((rateLimitedUntil! - Date.now()) / 1000),
      )
      return {
        state: 'rate_limited',
        maxLastSyncedAt: null,
        perDomain: [],
        retryAfterSec,
      }
    }

    const entries = Object.entries(domainStatus)

    // 2. Syncing — top-level isSyncing flag OR any per-domain entry actively
    // fetching. The top-level guard covers the window between Sync/Reset click
    // and the first socket `sync:domain:progress` event (which can be many
    // seconds on a long fan-out sync). Per-domain `fetching` still wins on
    // chip text for the popover — it carries individual domain timestamps.
    const anyDomainFetching = entries.some(([, e]) => e.status === 'fetching')
    if (topLevelIsSyncing || anyDomainFetching) {
      return {
        state: 'syncing',
        maxLastSyncedAt: null,
        perDomain: entries.map(([id, e]) => ({
          domainId: id,
          displayName: id,
          lastSyncedAt: e.lastSyncedAt,
          status: e.status === 'idle' ? 'never' : e.status,
        })),
      }
    }

    // 3. Never synced — no domains registered yet
    if (entries.length === 0) {
      return { state: 'never_synced', maxLastSyncedAt: null, perDomain: [] }
    }

    const errorEntries = entries.filter(([, e]) => e.status === 'error')
    const successEntries = entries.filter(([, e]) => e.status === 'success')

    // 4. Total failure — every domain errored.
    // Per-domain error detail is synthesised from domainStatus.errorCode so the
    // LastSyncChip popover can show category labels. This is independent of the
    // canvas-level error slots (rateLimitedUntil / authExpiredEnvelope / domainErrorStack).
    //
    // Caveat (PR #335 review): the synthesised envelope sets `vendor: id`
    // (the domainId — e.g. 'enrollment') because DomainStatusEntry doesn't
    // carry the integration vendor name. LastSyncChip.errorCategoryLabel
    // currently keys off `code` only and never reads `vendor`, so this is
    // safe — but downstream consumers must NOT rely on `vendor` here being
    // the real integration name. Pulling the real vendor through requires
    // widening DomainStatusEntry, tracked as follow-on tech debt.
    if (errorEntries.length === entries.length) {
      return {
        state: 'total_failure',
        maxLastSyncedAt: null,
        perDomain: entries.map(([id, e]) => ({
          domainId: id,
          displayName: id,
          lastSyncedAt: e.lastSyncedAt,
          status: 'error' as const,
          error: e.errorCode
            ? ({ code: e.errorCode, message: e.errorUserMessage ?? '', vendor: id } as SyncErrorEnvelope)
            : undefined,
        })),
      }
    }

    // 5. Partial failure — some but not all domains errored.
    // Same vendor-caveat as the total_failure arm above.
    if (errorEntries.length > 0) {
      return {
        state: 'partial_failure',
        maxLastSyncedAt: null,
        perDomain: entries.map(([id, e]) => ({
          domainId: id,
          displayName: id,
          lastSyncedAt: e.lastSyncedAt,
          status: e.status === 'error' ? 'error' : e.status === 'success' ? 'success' : e.status === 'idle' ? 'never' : 'fetching',
          error: e.status === 'error' && e.errorCode
            ? ({ code: e.errorCode, message: e.errorUserMessage ?? '', vendor: id } as SyncErrorEnvelope)
            : undefined,
        })),
      }
    }

    // 6 & 7. All success — check staleness
    const allLastSyncedAt = successEntries
      .map(([, e]) => e.lastSyncedAt)
      .filter((s): s is string => s != null)

    // Never synced — domains exist but none have ever completed
    if (allLastSyncedAt.length === 0) {
      return {
        state: 'never_synced',
        maxLastSyncedAt: null,
        perDomain: entries.map(([id, e]) => ({
          domainId: id,
          displayName: id,
          lastSyncedAt: null,
          status: e.status === 'idle' ? 'never' : e.status,
        })),
      }
    }

    const maxLastSyncedAt = allLastSyncedAt.reduce((a, b) => (a > b ? a : b))
    const ageMs = Date.now() - new Date(maxLastSyncedAt).getTime()
    const ONE_HOUR_MS = 60 * 60 * 1000

    const perDomain: LastSyncPerDomainEntry[] = entries.map(([id, e]) => ({
      domainId: id,
      displayName: id,
      lastSyncedAt: e.lastSyncedAt,
      status: e.status === 'idle' ? 'never' : e.status,
    }))

    return {
      state: ageMs < ONE_HOUR_MS ? 'synced_recent' : 'synced_stale',
      maxLastSyncedAt,
      perDomain,
    }
  },
)

// ── Conversational edit flow selectors ─────────────────────────────────────

export const selectEditingPanelId = (state: RootState): string | null =>
  state.dashboardView.editingPanelId

export const selectEditingPanelTitle = (state: RootState): string | null =>
  state.dashboardView.editingPanelTitle

export const selectChatMode = (state: RootState): 'free' | 'panel' =>
  state.dashboardView.chatMode

export const selectPendingEditProposal = (state: RootState): EditProposal | null =>
  state.dashboardView.pendingEditProposal

// ── Slice 3.2 — Sync error UX with panel preservation ────────────────────────

/**
 * Returns the `domains_failed` snapshot from the most recent
 * `sync:dashboard:complete` event. Empty when the latest sync had no
 * unavailable-credential skips.
 *
 * Drives `<SyncErrorBanner />` mount and the per-panel "(cached)" suffix.
 *
 * TDD §13.2, Brand guide §37f.
 */
export const selectDomainsFailed = (
  state: RootState,
): SyncDomainFailure[] => state.dashboardView.domainsFailed

/**
 * Memoised: returns the set of qualified domain ids whose backing credential
 * was unavailable on the latest sync. Used by PanelCanvasV1 to derive each
 * panel's `isCached` boolean.
 *
 * Selector identity is stable across dispatches that don't change
 * `domainsFailed` — guards downstream `React.memo` boundaries.
 */
export const selectFailedDomainIds = createSelector(
  [selectDomainsFailed],
  (domainsFailed): Set<string> =>
    new Set(domainsFailed.map(d => d.domainId)),
)

export const selectSyncErrorDismissed = (state: RootState): boolean =>
  state.dashboardView.syncErrorDismissed

/**
 * Whether the §37f sync-error banner should currently be rendered.
 * True when the latest sync reported at least one unavailable-credential
 * skip AND the user has not dismissed the banner for this sync cycle.
 */
export const selectShouldShowSyncErrorBanner = (state: RootState): boolean =>
  state.dashboardView.domainsFailed.length > 0 &&
  !state.dashboardView.syncErrorDismissed
